import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { extractApiError, setToken } from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

/**
 * Reset-password page. Reads `?token=` from the URL and lets the
 * participant submit a new password.
 *
 * Error-state semantics (2026-09-23 investigation, § C3):
 *   - We ONLY consume the reset token on a successful POST to
 *     /auth/reset-password. Visiting this page does NOT consume it,
 *     and neither does a failed submit (weak password, mismatched
 *     confirm). This is verified by the scanner-simulation script
 *     at /app/memory/audit_2026-09/scripts/reset_semantics_and_scanner_sim.py.
 *   - When the API returns 400 "invalid or already used" (row 1/2/4
 *     of the backend failure-mode map) or 400 "has expired" (row 3),
 *     we surface a distinct "link no longer valid" state with a
 *     button that deep-links to /forgot-password so the user can
 *     request a fresh link.
 *   - When the API returns 422 (Pydantic body-validation failure —
 *     weak password, missing token), we surface the validator message
 *     as an inline error but keep the form usable so the user can
 *     correct the password and resubmit. The token is NOT consumed
 *     in that case.
 *   - 5xx and transport failures (network, CORS, WAF) render as a
 *     "we couldn't reach the server" state — NOT relabelled as
 *     "expired". This distinction was Mark's specific request.
 */
function classifyResetError(err) {
  const status = err?.response?.status;
  const detailStr = extractApiError(err, "").toString();
  const looksExpired = /has expired/i.test(detailStr);
  const looksInvalid = /invalid or has already been used|invalid/i.test(detailStr);

  if (status === 400 && looksInvalid) {
    return { kind: "link_dead", message: "This reset link is no longer valid. It may have been used already, or a different reset was requested since." };
  }
  if (status === 400 && looksExpired) {
    return { kind: "link_expired", message: "This reset link has expired. Reset links are usable for 30 minutes after they were requested." };
  }
  if (status === 400) {
    return { kind: "link_dead", message: detailStr || "This reset link is no longer valid." };
  }
  if (status === 422) {
    return { kind: "field_error", message: detailStr || "Please check your new password and try again." };
  }
  if (status === 429) {
    return { kind: "rate_limited", message: detailStr || "Too many attempts. Please wait a minute before trying again." };
  }
  if (status && status >= 500) {
    return { kind: "server_error", message: "We couldn't reach the server. Please try again in a moment — your reset link has NOT been used yet." };
  }
  if (!err?.response) {
    return { kind: "network_error", message: "We couldn't reach the server. Check your connection and try again — your reset link has NOT been used yet." };
  }
  return { kind: "unknown", message: detailStr || "Something went wrong. Please try again." };
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setTokenField] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errState, setErrState] = useState(null); // {kind, message} | null
  const [done, setDone] = useState(false);

  const linkDeadKinds = new Set(["link_dead", "link_expired"]);
  const showRequestNewLink = errState && linkDeadKinds.has(errState.kind);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrState(null);
    if (password !== confirmPassword) {
      setErrState({ kind: "field_error", message: "Passwords do not match." });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setToken(null);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setErrState(classifyResetError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-base text-fg-primary flex flex-col">
      <header className="border-b border-border-subtle bg-bg-surface">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Wordmark size="sm" testid="reset-header-wordmark" /></Link>
          <Link to="/login" data-testid="reset-to-login-link" className="text-sm text-fg-secondary hover:text-fg-primary transition-colors duration-200">Back to log in</Link>
        </div>
      </header>

      <main className="flex-1 flex items-start sm:items-center justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-md">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary">Set a new password</h1>
          <p className="mt-2 text-sm text-fg-secondary">
            Reset links expire 30 minutes after they were issued and can be used once.
          </p>

          {done && (
            <Alert className="mt-6 border-progress-teal-bg bg-progress-teal-bg/40" data-testid="reset-success">
              <AlertDescription className="text-fg-primary">
                Your password has been updated. Redirecting you to log in…
              </AlertDescription>
            </Alert>
          )}

          {errState && !done && (
            <div className="mt-6 space-y-3" data-testid={`reset-error-${errState.kind}`}>
              <Alert variant="destructive" data-testid="reset-error">
                <AlertDescription>{errState.message}</AlertDescription>
              </Alert>
              {showRequestNewLink && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/forgot-password"
                    data-testid="reset-request-new-link-btn"
                    className="inline-flex justify-center items-center rounded-md bg-accent-warm text-bg-base hover:bg-accent-warm-hover px-4 py-2 text-sm font-medium transition-colors duration-200"
                  >
                    Request a new reset link
                  </Link>
                  <Link
                    to="/login"
                    data-testid="reset-error-back-to-login"
                    className="inline-flex justify-center items-center rounded-md border border-border-subtle text-fg-primary hover:bg-bg-surface px-4 py-2 text-sm font-medium transition-colors duration-200"
                  >
                    Back to log in
                  </Link>
                </div>
              )}
            </div>
          )}

          {!showRequestNewLink && (
            <form onSubmit={onSubmit} className="mt-6 space-y-5" data-testid="reset-form" autoComplete="on">
              {/*
                Browser-autofill hygiene (fix cycle 2026-09-24):
                  1. The reset token arrives via `?token=` in the URL and is
                     kept in component state; it is NOT rendered as a visible
                     text input, so no browser can mistake it for a username
                     (which was Mark's bug: he saw a "long letters-and-numbers
                     string" saved as his login identifier).
                  2. A hidden but accessible `autoComplete="username"` field
                     with an empty value signals to Chrome/Firefox that this
                     form is a credential form for an unknown user, so the
                     new password is categorised correctly without a bogus
                     username being saved. Kept off-screen (not display:none)
                     so autofill actually reads it.
                  3. Both password inputs use `autoComplete="new-password"`
                     so browsers offer "Update password?" against the site
                     origin instead of proposing an old current password.
              */}
              <input
                type="text"
                name="username"
                autoComplete="username"
                aria-hidden="true"
                tabIndex={-1}
                readOnly
                value=""
                data-testid="reset-username-hint"
                style={{
                  position: "absolute",
                  left: "-10000px",
                  width: "1px",
                  height: "1px",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
              <input
                type="hidden"
                name="reset_token"
                data-testid="reset-token-hidden"
                value={token}
                readOnly
              />
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  name="new-password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  data-testid="reset-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-bg-surface border-border-subtle text-fg-primary"
                />
                <p className="text-xs text-fg-secondary">Minimum 8 characters. Must contain a letter and a number.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input
                  id="confirm"
                  name="new-password-confirm"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  data-testid="reset-confirm-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-bg-surface border-border-subtle text-fg-primary"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || !token || !password || !confirmPassword}
                data-testid="reset-submit-btn"
                className="w-full bg-accent-warm text-bg-base hover:bg-accent-warm-hover disabled:opacity-50"
              >
                {submitting ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
